import axios from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const API_URL = API_BASE_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Required for CORS with credentials
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors – redirect to login only when not already on login/register (avoid refresh on wrong credentials)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      const isAuthRequest = /^\/?auth\/(login|register|google)/.test(requestUrl);
      if (!isAuthRequest) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = async (username, password) => {
  const response = await api.post('/auth/login', { username, password });
  return response.data;
};

export const register = async (username, password) => {
  const response = await api.post('/auth/register', { username, password });
  return response.data;
};

export const loginWithGoogle = async (credential) => {
  const response = await api.post('/auth/google', { credential });
  return response.data;
};

export const verifyToken = async () => {
  const response = await api.get('/auth/verify');
  return response.data;
};

// User profile / settings
export const getMe = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

export const updateMe = async (data) => {
  const response = await api.patch('/auth/me', data);
  return response.data;
};

export const changePassword = async (currentPassword, newPassword) => {
  const response = await api.post('/auth/change-password', { currentPassword, newPassword });
  return response.data;
};

export const initAdmin = async () => {
  const response = await api.post('/auth/init');
  return response.data;
};

// Accounts
export const getAccounts = async (platform) => {
  const response = await api.get('/accounts', { params: { platform } });
  return response.data;
};

export const getAccountById = async (id) => {
  const response = await api.get(`/accounts/${id}`);
  return response.data;
};

export const createAccount = async (accountData) => {
  const response = await api.post('/accounts', accountData);
  return response.data;
};

export const updateAccount = async (id, accountData) => {
  const response = await api.put(`/accounts/${id}`, accountData);
  return response.data;
};

export const deleteAccount = async (id) => {
  const response = await api.delete(`/accounts/${id}`);
  return response.data;
};

export const unblockAccount = async (id) => {
  const response = await api.post(`/accounts/${id}/unblock`);
  return response.data;
};

// Events
export const getEvents = async (platform, active) => {
  const response = await api.get('/events', { params: { platform, active } });
  return response.data;
};

export const getEventById = async (id) => {
  const response = await api.get(`/events/${id}`);
  return response.data;
};

export const createEvent = async (eventData) => {
  const response = await api.post('/events', eventData);
  return response.data;
};

export const updateEvent = async (id, eventData) => {
  const response = await api.put(`/events/${id}`, eventData);
  return response.data;
};

export const deleteEvent = async (id) => {
  const response = await api.delete(`/events/${id}`);
  return response.data;
};

export const loadEventMap = async (id, useProxy = true, headless = false, runLocation = 'local') => {
  // Long timeout for map loading (can take 60+ seconds)
  const response = await api.post(`/events/${id}/load-map`, 
    { useProxy, headless, runLocation },
    { timeout: 180000 } // 3 minutes timeout
  );
  return response.data;
};

// Cron Jobs
export const getCronJobs = async () => {
  const response = await api.get('/cron');
  return response.data;
};

export const getCronJobById = async (id) => {
  const response = await api.get(`/cron/${id}`);
  return response.data;
};

export const createCronJob = async (cronJobData) => {
  const response = await api.post('/cron', cronJobData);
  return response.data;
};

export const updateCronJob = async (id, cronJobData) => {
  const response = await api.put(`/cron/${id}`, cronJobData);
  return response.data;
};

export const deleteCronJob = async (id) => {
  const response = await api.delete(`/cron/${id}`);
  return response.data;
};

export const executeCronJob = async (id) => {
  const response = await api.post(`/cron/${id}/execute`);
  return response.data;
};

// Logs
export const getLogs = async (type, limit = 100) => {
  const response = await api.get('/logs', { params: { type, limit } });
  return response.data;
};

export const createLog = async (logData) => {
  const response = await api.post('/logs', logData);
  return response.data;
};

export const cleanupLogs = async (daysOld = 30) => {
  const response = await api.delete('/logs/cleanup', { data: { daysOld } });
  return response.data;
};

// Bot
export const runBot = async (eventId, accountIds, enableLiveView = false, useProxy = false, maxPrice = null, headless = null, runLocation = 'local') => {
  const response = await api.post('/bot/run', { eventId, accountIds, enableLiveView, useProxy, maxPrice, headless, runLocation });
  return response.data;
};

export const getBotStatus = async () => {
  const response = await api.get('/bot/status');
  return response.data;
};

export const stopBotSession = async (sessionId) => {
  const response = await api.post('/bot/stop', { sessionId });
  return response.data;
};

// Admins
export const getAdmins = async () => {
  const response = await api.get('/admins');
  return response.data;
};

export const getAdminById = async (id) => {
  const response = await api.get(`/admins/${id}`);
  return response.data;
};

export const createAdmin = async (adminData) => {
  const response = await api.post('/admins', adminData);
  return response.data;
};

export const updateAdmin = async (id, adminData) => {
  const response = await api.put(`/admins/${id}`, adminData);
  return response.data;
};

export const deleteAdmin = async (id) => {
  const response = await api.delete(`/admins/${id}`);
  return response.data;
};

// Emails / Gmail
export const getEmails = async () => {
  const response = await api.get('/emails');
  return response.data;
};

export const getEmailBody = async (emailId) => {
  const response = await api.get(`/emails/${emailId}`);
  return response.data;
};

export const initiateGoogleOAuth = async () => {
  const response = await api.get('/emails/auth/google');
  return response.data;
};

export const disconnectGmail = async () => {
  const response = await api.delete('/emails/disconnect');
  return response.data;
};

// Reservations
export const getReservations = async () => {
  const response = await api.get('/reservations');
  return response.data;
};

// Workflows (v2 builder)
export const getWorkflows = async () => {
  const response = await api.get('/workflows');
  return response.data;
};

export const getWorkflowById = async (id) => {
  const response = await api.get(`/workflows/${id}`);
  return response.data;
};

/** Stiahnutie HTML stránky cez backend (obchádza CSP frame-ancestors). Vráti HTML string. */
export const getPreviewPageHtml = async (url) => {
  const response = await api.get('/workflows/preview-page', {
    params: { url },
    responseType: 'text',
  });
  return response.data;
};

export const createWorkflow = async (data) => {
  const response = await api.post('/workflows', data);
  return response.data;
};

export const updateWorkflow = async (id, data) => {
  const response = await api.put(`/workflows/${id}`, data);
  return response.data;
};

export const deleteWorkflow = async (id) => {
  const response = await api.delete(`/workflows/${id}`);
  return response.data;
};

export const publishWorkflow = async (id) => {
  const response = await api.post(`/workflows/${id}/publish`);
  return response.data;
};

export const unpublishWorkflow = async (id) => {
  const response = await api.post(`/workflows/${id}/unpublish`);
  return response.data;
};

export const runWorkflow = async (id, { eventId, accountId, enableLiveView, useProxy, maxPrice, headless, fresh, recordingMode, nodes, edges, query, body: bodyParams }) => {
  const reqBody = {
    eventId,
    accountId,
    enableLiveView,
    useProxy,
    maxPrice,
    headless,
    fresh,
    recordingMode,
  };
  if (Array.isArray(nodes)) reqBody.nodes = nodes;
  if (Array.isArray(edges)) reqBody.edges = edges;
  if (query && Object.keys(query).length > 0) reqBody.query = query;
  if (bodyParams && Object.keys(bodyParams).length > 0) reqBody.body = bodyParams;
  const response = await api.post(`/workflows/${id}/run`, reqBody);
  return response.data;
};

/** Zoznam behov pre workflow (pre históriu) */
export const getWorkflowRuns = async (workflowId, page = 1, limit = 10) => {
  const response = await api.get(`/workflows/${workflowId}/runs`, { params: { page, limit } });
  return response.data;
};

/** Posledné behy (všetky workflow) */
export const getRecentWorkflowRuns = async (page = 1, limit = 10) => {
  const response = await api.get('/workflows/recent-runs', { params: { page, limit } });
  return response.data;
};

/** Jeden beh (pri pollingu sa aktualizuje stav z Pythonu) */
export const getWorkflowRunById = async (runId) => {
  const response = await api.get(`/workflows/runs/${runId}`);
  return response.data;
};

/** Aplikovať autorepair z behu (aktualizuje fragment v workflow podľa repairedFragments) */
export const applyWorkflowRunRepair = async (runId) => {
  const response = await api.post(`/workflows/runs/${runId}/apply-repair`);
  return response.data;
};

/** Posledný snapshot pre workflow (po skončení behu) */
export const getLastSnapshot = async (workflowId) => {
  const response = await api.get('/workflows/snapshots/last', { params: { workflowId } });
  return response.data;
};

/** Raw HTML snapshotu (pre zobrazenie v modale) */
export const getSnapshotHtml = async (snapshotId) => {
  const response = await api.get(`/workflows/snapshots/${snapshotId}/html`, { responseType: 'text' });
  return response.data;
};

/** Spustiť n8n workflow (manual run). query/body sú voliteľné vstupné parametre pre workflow. */
export const runN8nWorkflow = async (id, { query = {}, body = {} } = {}) => {
  const params = query && Object.keys(query).length > 0 ? query : undefined;
  const data = body && Object.keys(body).length > 0 ? body : undefined;
  const response = await api.post(`/workflows/${id}/n8n/run`, data ?? {}, { params });
  return response.data;
};

// AI Connectors (pre n8n workflow)
export const getAiConnectors = async () => {
  const response = await api.get('/ai-connectors');
  return response.data;
};

export const createAiConnector = async (data) => {
  const response = await api.post('/ai-connectors', data);
  return response.data;
};

export const updateAiConnector = async (id, data) => {
  const response = await api.put(`/ai-connectors/${id}`, data);
  return response.data;
};

export const deleteAiConnector = async (id) => {
  const response = await api.delete(`/ai-connectors/${id}`);
  return response.data;
};

// User Webhooks (Discord, Slack, Teams, Custom – priradené k userId)
export const getUserWebhooks = async () => {
  const response = await api.get('/user-webhooks');
  return response.data;
};

export const createUserWebhook = async (data) => {
  const response = await api.post('/user-webhooks', data);
  return response.data;
};

export const updateUserWebhook = async (id, data) => {
  const response = await api.put(`/user-webhooks/${id}`, data);
  return response.data;
};

export const deleteUserWebhook = async (id) => {
  const response = await api.delete(`/user-webhooks/${id}`);
  return response.data;
};

export default api;
